import React from 'react';
import { Card, CardContent } from '../ui/card';
import { AlertTriangle, Layers, Clock, Sparkles } from 'lucide-react';
import { scrollAndFlash } from '../../lib/highlight';

// Compact alert tiles shown as a row above the PO table (budget / exhausted / expiring).
export const POAlertBanners = ({ atRiskPos, exhaustedPos, expiringPos = [], recentPos = [], onAddExtension }) => {
  const tiles = [];

  if (atRiskPos.length > 0) {
    tiles.push(
      <Card key="at-risk" className="border-amber-400 bg-amber-50 h-full" data-testid="po-utilization-alert">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="font-semibold text-amber-900 text-sm">
              {atRiskPos.length} PO{atRiskPos.length > 1 ? 's' : ''} nearing budget limit
            </p>
          </div>
          <p className="text-xs text-amber-700 mb-2">Over 80% utilized — review and renew.</p>
          <div className="flex flex-wrap gap-1.5">
            {atRiskPos.map(po => (
              <div key={po.id} className="flex items-center gap-1 text-[11px] bg-white border border-amber-200 rounded-full px-2 py-0.5 text-amber-800 font-medium" data-testid={`at-risk-badge-${po.id}`}>
                <span>{po.po_number}</span>
                <span className={(po.utilization_percentage || 0) >= 95 ? 'text-red-600 font-bold' : ''}>{po.utilization_percentage}%</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (exhaustedPos.length > 0) {
    tiles.push(
      <Card key="exhausted" className="border-red-400 bg-red-50 h-full" data-testid="po-exhausted-alert">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="font-semibold text-red-900 text-sm">
              {exhaustedPos.length} PO{exhaustedPos.length > 1 ? 's' : ''} exhausted — awaiting Extension
            </p>
          </div>
          <p className="text-xs text-red-700 mb-2">Fully consumed — add an Extension to keep billing.</p>
          <div className="flex flex-wrap gap-1.5">
            {exhaustedPos.map(po => (
              <button
                key={po.id}
                type="button"
                onClick={() => onAddExtension(po)}
                className="flex items-center gap-1 text-[11px] bg-white border border-red-200 rounded-full px-2 py-0.5 text-red-800 font-medium hover:bg-red-100 transition-colors"
                data-testid={`exhausted-alert-badge-${po.id}`}
              >
                <span>{po.po_number}</span>
                <span className="text-red-600 font-bold">100%</span>
                <Layers className="w-3 h-3" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (expiringPos.length > 0) {
    tiles.push(
      <Card key="expiring" className="border-amber-400 bg-amber-50 h-full" data-testid="po-expiring-alert">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="font-semibold text-amber-900 text-sm">
              {expiringPos.length} PO{expiringPos.length > 1 ? 's' : ''} expiring within 30 days
            </p>
          </div>
          <p className="text-xs text-amber-700 mb-2">Last delivery date approaching — plan a renewal.</p>
          <div className="flex flex-wrap gap-1.5">
            {expiringPos.map(po => (
              <div key={po.id} className="flex items-center gap-1 text-[11px] bg-white border border-amber-200 rounded-full px-2 py-0.5 text-amber-800 font-medium" data-testid={`expiring-badge-${po.id}`}>
                <span>{po.po_number}</span>
                <span className={(po.validity_days_left ?? 99) <= 7 ? 'text-red-600 font-bold' : ''}>
                  {po.validity_days_left === 0 ? 'today' : `${po.validity_days_left}d`}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Always-on informational tile: POs / Extensions created in the last 7 days
  tiles.push(
    <Card key="recent" className="border-emerald-400 bg-emerald-50 h-full" data-testid="po-recent-alert">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <p className="font-semibold text-emerald-900 text-sm">
            {recentPos.length} Newly Added PO{recentPos.length === 1 ? '' : 's'}
          </p>
        </div>
        <p className="text-xs text-emerald-700 mb-2">POs / Extensions added in the last 7 days.</p>
        {recentPos.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {recentPos.map(po => (
              <button
                key={po.id}
                type="button"
                onClick={() => scrollAndFlash(`po-row-${po.id}`)}
                className="flex items-center gap-1 text-[11px] bg-white border border-emerald-200 rounded-full px-2 py-0.5 text-emerald-800 font-medium hover:bg-emerald-100 transition-colors"
                data-testid={`recent-badge-${po.id}`}
              >
                <span>{po.po_number}</span>
                {po.po_type === 'extension' && <span className="text-amber-600">ext</span>}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-emerald-700/70">No new POs in the last 7 days.</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="po-alert-tiles">
      {tiles}
    </div>
  );
};

export default POAlertBanners;
